import React, { useState } from 'react';
import { Tabs } from 'antd';
import styled from 'styled-components';

const Wrapper = styled.div`
  background: #fff;
  padding: 1.5rem;
`;

const Title = styled.h2`
  font-size: 2.25rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 1.5rem;
  color: #480D05;
`;

const Subtitle = styled.p`
  font-size: 1.125rem;
  text-align: center;
  margin-bottom: 2rem;
  color: #221511;
`;

const StyledTabs = styled(Tabs)`
  .ant-tabs-nav-list {
    border: none;
    display: flex;
    justify-content: center;
    width: 100%;
  }
  .ant-tabs-tab {
    background: #faf5f3;
    padding: 10px 20px;
    border-radius: 10px;
    color: #000;
    border: solid 1px #faf5f3;
    font-size: 1rem;
    font-weight: 500;
    margin-right: 8px;
    transition: background 0.2s, border 0.2s;
  }
  .ant-tabs-tab-active {
    background: #fff;
    color: #f87b15 !important;
    border: solid 1px #f87b15;
  }
  .ant-tabs-nav::before {
    border: none !important;
  }
  .ant-tabs-ink-bar {
    display: none !important;
  }
  .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: #f87b15 !important;
  }
`;

const SubTabsWrapper = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  justify-content: center;
`;

const SubTabButton = styled.button<{ active: boolean }>`
  background: ${({ active }) => (active ? '#fff' : '#faf5f3')};
  color: ${({ active }) => (active ? '#f87b15' : '#221511')};
  border: 1.5px solid ${({ active }) => (active ? '#f87b15' : 'transparent')};
  border-radius: 1.5rem;
  padding: 0.5rem 1.5rem;
  font-size: 1.1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
`;

const FaqList = styled.div`
  border-radius: 0.375rem;
  padding: 0.5rem 0;
`;

const FaqItem = styled.div`
  margin-bottom: 1rem;
  padding: 1rem 1.5rem;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
  border-radius: 0.5rem;
  background: #fafafa;
`;

const FaqQuestion = styled.button`
  display: flex;
  justify-content: space-between;
  width: 100%;
  align-items: center;
  text-align: left;
  color: #480D05;
  background: none;
  border: none;
  font-size: 1.25rem;
  font-weight: 500;
  cursor: pointer;
  outline: none;
  transition: color 0.2s;
  &:hover, &:focus {
    color: #221511;
  }
`;

const FaqToggle = styled.span`
  font-size: 1rem;
  color: #555;
  background: #fff;
  border: 1px solid #221511;
  border-radius: 0.5rem;
  padding: 0.25rem 1rem;
  margin-left: 1rem;
  font-weight: 500;
`;

const FaqAnswer = styled.div`
  margin-top: 0.5rem;
  color: #999;
  font-size: 1.125rem;
  white-space: pre-line;
`;

// New FAQ data structure
const faqData = [
  {
    key: "1",
    label: "Setup, Power & Connectivity",
    subcategories: [
      {
        key: "general",
        label: "General",
        faqs: [
          {
            question: "Can I share my camera with others to view at the same time?",
            answer:
              "Yes! You can easily share your camera with others so multiple people can view it live from different places. You can go to Profile and select Shared Users. The other user will need to download the Sensforge app to accept the invite.",
          },
          {
            question: "How to reconnect my camera after changing Wi-Fi?",
            answer:
              "1. Open the Sensforge app and delete the device.\n2. Power off the camera.\n3. Power it back on.\n4. Use the app to scan the QR code on the device and go through the pairing process again.",
          },
          {
            question: "Livestream is lagging so we couldn't watch in real time.",
            answer:
              "Live streaming typically has a natural delay of about 4 seconds. If you experience lag beyond that, it usually indicates that the router's signal is weak or not providing sufficient coverage at that distance. Unfortunately, issues related to router signal strength are outside of what we can improve from our side.",
          },
        ],
      },
      {
        key: "pan-tilt",
        label: "Pan-Tilt Camera",
        faqs: [
          {
            question: "The manual text is too small. Where can I find the setup instructions?",
            answer:
              "We've created a step-by-step video guide to walk you through the setup process:\nWatch the video here (https://www.youtube.com/watch?v=JogRB9YksSo)",
          },
          {
            question: "Is the Pan-Tilt camera easy to install? Can it be mounted or placed on a shelf?",
            answer:
              "Yes! It is plug-and-play. You can place it on any flat surface or mount to a wall or ceiling using the included accessories.",
          },
          {
            question: "Does the camera run on power or batteries?",
            answer:
              "It runs on a power adapter (included). A stable power source ensures uninterrupted performance.",
          },
          {
            question: "Does the camera work with both 2.4GHz and 5GHz Wi-Fi?",
            answer:
              "The camera works with both 2.4GHz and 5GHz Wi-Fi for wider compatibility and stable connectivity.",
          },
          {
            question: "Can I control the camera through Alexa or Google Assistant?",
            answer:
              "Yes! It supports Amazon Alexa for voice control and live view.",
          },
        ],
      },
      {
        key: "bird-feeder",
        label: "Bird Feeder Camera",
        faqs: [
          {
            question: "Why is it charging slowly?",
            answer:
              "Try plugging the device into a more powerful power source first, such as a wall outlet instead of a lower-powered source like a computer USB port or a basic power strip. Fully charge the device using the cord before switching to the solar panel for continued charging. High activity, constant motion recording, or running on High Performance mode drains battery faster. Switch to Sleep/Wake Mode (records only when birds are detected) under Work Mode to extend battery life. Ensure the solar panel is facing direct sunlight and that the panel is clean. Cloudy days will charge slower than sunny days.",
          },
          {
            question: "Why does it have two charging ports?",
            answer:
              "Both ports should work when plugged into a wall outlet, but the top port cannot be used to connect to the solar panel.",
          },
          {
            question: "What should I do when it's getting lots of squirrels on?",
            answer:
              "Try using squirrel-resistant bird seeds like safflower or seeds with hot pepper coating. It is recommended to install the smart bird feeder on a pole with a baffle below the feeder and enable the squirrel repellent feature in the app. Keep in mind that squirrels can become accustomed to the squirrel repellent sound.",
          },
          {
            question: "The roof isn't protecting the seeds inside.",
            answer:
              "Align the back of the roof with the bird house so that there is shade over the seeds in the front. The roof is magnetic so it can be easily adjusted.",
          },
          {
            question: "The birdhouse is too far away to reach the Wi-Fi signal.",
            answer:
              "Bring the birdhouse closer to the house/router, or speak with your internet provider about getting an extender.",
          },
          {
            question: "Why is battery draining really quickly?",
            answer:
              "Go to your settings to edit the Work Mode: turn off motion detection and lower the sensitivity.",
          },
        ],
      },
    ],
  },
  {
    key: "2",
    label: "Video Quality & Features",
    subcategories: [
      {
        key: "general",
        label: "General",
        faqs: [
          {
            question: "Why do I fail to download videos?",
            answer:
              "Please check whether Cloud Storage is enabled in your app settings by going your camera's settings > Storage Setting.",
          },
          {
            question: "Is there any way to speed up playback?",
            answer:
              "Go to the Livestream of your camera, tap 24/7 tab, find the time frame you want to watch, then adjust the speed directly on the screen. The speed is represented by a number and 'x'.",
          },
          {
            question: "What resolution does the Sensforge camera support?",
            answer:
              "The camera records in sharp 2.5K Full HD, delivering crystal-clear video with fine detail.",
          },
          {
            question: "Does the camera offer night vision or color night vision?",
            answer:
              "Yes. It features advanced color night vision, ensuring clear images even in low light.",
          },
          {
            question: "Is the video feed live or does it have a delay?",
            answer:
              "The video feed is live with minimal latency, offering real-time monitoring.",
          },
        ],
      },
      {
        key: "pan-tilt",
        label: "Pan-Tilt Camera",
        faqs: [
          {
            question: "Can the camera pan and tilt to cover different areas?",
            answer:
              "Yes, with remote-controlled 360° pan and 114° tilt, you can monitor the full room without blind spots.",
          },
          {
            question: "Can the camera auto-track movement?",
            answer:
              "Yes. When motion is detected, the camera can automatically follow the moving object within its range for better tracking.",
          },
        ],
      },
    ],
  },
  {
    key: "3",
    label: "Smart Detection & Alerts",
    subcategories: [
      {
        key: "general",
        label: "General",
        faqs: [
          {
            question: "Does the camera send instant alerts when it detects motion?",
            answer:
              "Yes. You can opt into real-time push notifications when activity is detected. To adjust the frequency of the notifications, you can fine-tune the sensitivity or choose specific detection types like humans, pets, or unusual sounds to suit your preferences.",
          },
          {
            question: "Can I customize motion detection zones?",
            answer:
              "Yes. You can set specific areas within the camera's view to monitor for detection, reducing unwanted alerts.",
          },
          {
            question: "Will I get too many false notifications?",
            answer:
              "The AI filtering helps minimize false alarms by focusing on relevant activity like people and pets.",
          },
          {
            question: "How do I turn off notification sounds or mute alerts?",
            answer:
              "If you're referring to muting the notification sound, please go to your phone's settings → Notifications → Sensforge app → and adjust the sound settings to Silent or Mute.\nIf you're trying to stop receiving notifications altogether, you can open the Sensforge app, go to Device Settings → Notification Settings, and toggle Message Notifications off.",
          },
          {
            question: "Does it support sound detection or abnormal noise alerts?",
            answer:
              "Yes. The camera can also detect unexpected sounds like baby cries or loud noises and send you alerts.",
          },
          {
            question: "Can I schedule detection alerts during specific hours?",
            answer:
              "Absolutely. You can customize alert schedules based on your daily routine to avoid unnecessary notifications.",
          },
        ],
      },
      {
        key: "pan-tilt",
        label: "Pan-Tilt Camera",
        faqs: [
          {
            question: "Can it detect and distinguish between people, pets, and general motion?",
            answer:
              "Yes. Our built-in AI detection system can identify humans, pets, and other movement—with no subscription required.",
          },
          {
            question: "Does it support sound detection or abnormal noise alerts?",
            answer:
              "Yes. The camera can also detect unexpected sounds like baby cries or loud noises and send you alerts.",
          },
        ],
      },
      {
        key: "bird-feeder",
        label: "Bird Feeder Camera",
        faqs: [
          {
            question: "Motion detection is recording clips where there isn't a bird.",
            answer:
              "Check Detection Sensitivity settings — low sensitivity may miss smaller birds. Note: Motion detection will detect everything, including rain, wind, and shadows. It's recommended to turn off this setting. Instead, enable Bird Detection.",
          },
          {
            question: "What does the squirrel repellant do?",
            answer:
              "The squirrel repellent is an alarm sound that can be triggered with a squirrel is detected. Enable it in the app under Smart Detection > Squirrel Repellent. You can also manually trigger it from the livestream screen. Keep in mind that some squirrels may become accustomed to the sound. Mounting the bird feeder on a pole with a baffle is a good alternative.",
          },
        ],
      },
    ],
  },
  {
    key: "4",
    label: "Audio, Storage & Privacy",
    subcategories: [
      {
        key: "general",
        label: "General",
        faqs: [
          {
            question: "Is my footage encrypted and secure?",
            answer:
              "Yes. All data is encrypted during transmission and storage to protect your privacy.",
          },
          {
            question: "Is a subscription required for motion alerts or recordings?",
            answer:
              "No subscription is needed for AI detection, real-time alerts, or local SD card storage.",
          },
          {
            question: "How long can I store footage on the SD card?",
            answer:
              "Depending on your recording settings and card size. Footage can be stored for several days to weeks before auto-overwriting.",
          },
        ],
      },
      {
        key: "pan-tilt",
        label: "Pan-Tilt Camera",
        faqs: [
          {
            question: "Does the camera support two-way audio?",
            answer:
              "Yes. It features full-duplex two-way audio, allowing clear communication from app to camera and back—just like a phone call.",
          },
          {
            question: "Does the camera have a physical privacy shutter?",
            answer:
              "Yes. You can manually or remotely activate the privacy shutter for complete peace of mind.",
          },
        ],
      },
    ],
  },
];

export default function SupportQuestions() {
  const [mainTab, setMainTab] = useState('1');
  const [subTab, setSubTab] = useState(faqData[0].subcategories[0].key);
  const [expanded, setExpanded] = useState<number | null>(null);

  // Update subTab when mainTab changes
  React.useEffect(() => {
    const currentMain = faqData.find((tab) => tab.key === mainTab);
    if (currentMain) {
      setSubTab(currentMain.subcategories[0].key);
      setExpanded(null);
    }
  }, [mainTab]);

  const currentMain = faqData.find((tab) => tab.key === mainTab);
  const currentSub = currentMain?.subcategories.find((sub) => sub.key === subTab);

  const toggleExpand = (index: number) => {
    setExpanded(expanded === index ? null : index);
  };

  return (
    <Wrapper>
      <Title>Frequently Asked Questions</Title>
      <Subtitle>
        Find setup instructions, troubleshooting steps, and expert tips to get the most out of your Sensforge Cameras.
      </Subtitle>
      <StyledTabs
        activeKey={mainTab}
        onChange={setMainTab}
        tabBarGutter={16}
        items={faqData.map((tab) => ({
          label: tab.label,
          key: tab.key,
        }))}
      />
      {currentMain && (
        <SubTabsWrapper>
          {currentMain.subcategories.map((sub) => (
            <SubTabButton
              key={sub.key}
              active={subTab === sub.key}
              onClick={() => {
                setSubTab(sub.key);
                setExpanded(null);
              }}
            >
              {sub.label}
            </SubTabButton>
          ))}
        </SubTabsWrapper>
      )}
      <FaqList>
        {currentSub?.faqs.map((faq, idx) => (
          <FaqItem key={idx}>
            <FaqQuestion
              onClick={() => toggleExpand(idx)}
              aria-expanded={expanded === idx}
              aria-controls={`faq-answer-${idx}`}
            >
              <span>{faq.question}</span>
              <FaqToggle>
                {expanded === idx ? 'Hide details' : 'View details'}
              </FaqToggle>
            </FaqQuestion>
            {expanded === idx && (
              <FaqAnswer id={`faq-answer-${idx}`}>{faq.answer}</FaqAnswer>
            )}
          </FaqItem>
        ))}
      </FaqList>
    </Wrapper>
  );
}